import { CategoryFilterMode, FilterType } from '@sleed/bizcommerce-api';
import type { FilterTemplateValueToken } from '@sleed/bizcommerce-api';

/**
 * Per-filter settings of a filter-template value (the template's filter row), edited in the values
 * sheet's settings dialog. Mirrors the legacy admin FilterTemplateValues tooltip + category-config
 * modals (Core/client/admin/src/pages/Catalog/FilterTemplates/FilterTemplateValues.js):
 *   - toolTip ≤400 (any filter type),
 *   - categoryFilterMode / categoryNestingDepth / categoryLevel (Category filters only):
 *       mode unset (server default) or Subcategories → nesting depth applies (-1 = all depths),
 *       ByLevel → category level applies (-1 = all levels), Siblings → neither.
 * isFeatured / isImageFilter are plain flags toggled inline in the sheet.
 */
export const TOOLTIP_MAX = 400;

/** `null` = "server default" (the column is nullable server-side; the generated type omits null). */
export type CategoryFilterModeValue = CategoryFilterMode.Subcategories | CategoryFilterMode.Siblings | CategoryFilterMode.ByLevel | null;

export interface FilterTemplateValueSettings {
	toolTip: string;
	categoryFilterMode: CategoryFilterModeValue;
	categoryNestingDepth: number | null;
	categoryLevel: number | null;
}

export function isCategoryFilter(token: Pick<FilterTemplateValueToken, 'FilterType'>): boolean {
	return token.FilterType === FilterType.Category;
}

export function showsNestingDepth(mode: CategoryFilterModeValue): boolean {
	return mode === null || mode === CategoryFilterMode.Subcategories;
}

export function showsCategoryLevel(mode: CategoryFilterModeValue): boolean {
	return mode === CategoryFilterMode.ByLevel;
}

function toModeValue(mode: CategoryFilterMode | null | undefined): CategoryFilterModeValue {
	return mode === CategoryFilterMode.Subcategories || mode === CategoryFilterMode.Siblings || mode === CategoryFilterMode.ByLevel
		? mode
		: null;
}

export function tokenToSettings(token: FilterTemplateValueToken): FilterTemplateValueSettings {
	return {
		toolTip: token.toolTip ?? '',
		categoryFilterMode: toModeValue(token.categoryFilterMode),
		categoryNestingDepth: token.categoryNestingDepth ?? null,
		categoryLevel: token.categoryLevel ?? null
	};
}

/**
 * Merge the edited settings into the full row token for `update` (the endpoint replaces the row, so
 * every other column must round-trip). Fields that don't apply to the chosen mode are cleared, and
 * the category config is only written for Category filters.
 */
export function applySettings(token: FilterTemplateValueToken, settings: FilterTemplateValueSettings): FilterTemplateValueToken {
	const toolTip = settings.toolTip.trim();
	const next: FilterTemplateValueToken = { ...token, toolTip: toolTip ? toolTip.slice(0, TOOLTIP_MAX) : null };
	if (!isCategoryFilter(token)) return next;
	const mode = settings.categoryFilterMode;
	return {
		...next,
		// The generated enum type is non-nullable; `undefined` serialises as absent → null server-side.
		categoryFilterMode: mode ?? undefined,
		categoryNestingDepth: showsNestingDepth(mode) ? settings.categoryNestingDepth : null,
		categoryLevel: showsCategoryLevel(mode) ? settings.categoryLevel : null
	};
}

/** Template rows in storefront order: by `si`, ties broken by id so duplicate indexes still give a stable order. */
export function sortByStorefrontOrder(rows: FilterTemplateValueToken[]): FilterTemplateValueToken[] {
	return [...rows].sort((a, b) => (a.si ?? 0) - (b.si ?? 0) || (a.id ?? 0) - (b.id ?? 0));
}

/**
 * Move the row `activeId` to the position of `overId` and renumber the template 0..n-1, which is the
 * order `ApplyFilterTemplate` renders on the storefront. Returns only the rows whose `si` changed (the
 * `UpdateBulk` payload); legacy templates with duplicate indexes are normalised on their first move.
 */
export function reorderedRows(rows: FilterTemplateValueToken[], activeId: number, overId: number): FilterTemplateValueToken[] {
	const ordered = sortByStorefrontOrder(rows);
	const from = ordered.findIndex(row => row.id === activeId);
	const to = ordered.findIndex(row => row.id === overId);
	if (from === -1 || to === -1 || from === to) return [];
	const [moved] = ordered.splice(from, 1);
	ordered.splice(to, 0, moved);
	return ordered.flatMap((row, index) => (row.si === index ? [] : [{ ...row, si: index }]));
}
